import { useState, useEffect } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { X, Pencil, Trash2, MessageCircle, Eye, Link } from "lucide-react";
import { useUser, useCreateHiring, useGetHiring, useUpdateHiring, useDeleteHiring, useGetPermissions } from "../Use-auth";
import Pagination from "../ui/Pagination";
import SkeletonPage from "./SkeletonPage ";
import PermissionPopup from "./PermissionPopup";

const InfoRow = ({ label, value, className }) => (
    <tr className="text-[10px] sm:text-[12px]">
        <td className="px-2 py-2 sm:px-4 sm:py-2 font-medium text-gray-600 w-1/3">
            {label}
        </td>
        <td className="px-2 py-2 sm:px-4 sm:py-3 text-gray-800 ">
            {value || "-"}
        </td>
    </tr>
);

export default function HiringPage() {
    const { data: user } = useUser();
    const createHiring = useCreateHiring();
    const updateHiring = useUpdateHiring();
    const deleteHiring = useDeleteHiring();
    const isSubmitting = createHiring.isPending || updateHiring.isPending;
    const { data, isLoading: fetching } = useGetHiring();
    const hirings = data?.data || [];
    const { data: existingPermissions, refetch } = useGetPermissions();
    const [search, setSearch] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [editingHiring, setEditingHiring] = useState(null);
    const [filterDesignation, setFilterDesignation] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [openMessage, setOpenMessage] = useState(false);
    const [selectedHiring, setSelectedHiring] = useState(null);
    const [comment, setComment] = useState("");
    const [previewHiring, setPreviewHiring] = useState(null);
    const formsPerPage = 20;

    const emptyForm = {
        name: "",
        email: "",
        phone: "",
        gender: "",
        designation: "",
        interviewDate: "",
        resumeLink: "",
        portfolioLink: "",
        interviewer: "",
        interviewScheduleandTiming: "",
        interviewHour: "",
        interviewMinute: "",
        interviewAmPm: "AM",
        lastCTC: "",
        expectedCTC: "",
        notes: "",
        skillknowledge: "",
        workExperience: "",
        interviewMode: "",
        status: "",
    };

    const [formData, setFormData] = useState(emptyForm);

    // Single source of truth for all designations — used in both the filter dropdown and the form dropdown
    const designationList = [
        "Full Stack Developer",
        "Sales Executive",
        "Content Writer",
        "MERN Stack Developer",
        "WordPress Developer",
        "HR Executive",
        "HR Manager",
        "Dev TL (Development Team Lead)",
        "Design TL (Design Team Lead)",
        "Office Boy",
        "Recruiter",
        "Payroll Executive",
        "Training & Development Executive",
        "HR Operations Executive",
        "Sales Manager",
        "Business Development Manager",
        "Account Manager Executive",
        "Digital Marketing Executive",
        "Software Developer",
        "Web Developer",
        "E-commerce Developer",
        "Motion Designer",
        "Web Designer",
        "Sales Representative",
        // legacy options kept so existing records still display correctly
        "HR",
        "Human Resources (HR)",
        "Senior Sales Manager",
        "Project Manager",
        "Project Coordinator",
        "Shopify Developer",
        "Front-End Developer",
        "UI/UX Designer",
        "Graphic Designer",
        "Video Editor",
        "SEO Executive",
        "Website Tester (QA)",
    ];

    const isAdmin = user?.department === "Admin";

    const currentUserPermissions = isAdmin
        ? { hr: { hiring_records: true } }
        : existingPermissions?.find(p => p.userId === user?.userId);

    const canViewHome =
        isAdmin || currentUserPermissions?.hr?.hiring_records === true;

    const handleOpen = () => {
        setShowPopup(true);
        setSuccessMsg("");
        setErrorMsg("");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === "name" || name === "interviewer") {
            newValue = newValue.replace(/[^a-zA-Z ]/g, "");
        }

        if (name === "phone") {
            newValue = newValue.replace(/[^0-9]/g, "");
        }

        const maxLengths = {
            name: 100,
            email: 100,
            phone: 100,
            designation: 100,
            interviewer: 100,
            resumeLink: 100,
            portfolioLink: 100,
            status: 20,
        };

        if (maxLengths[name]) {
            newValue = newValue.slice(0, maxLengths[name]);
        }

        setFormData((prev) => ({ ...prev, [name]: newValue }));
    };

    const buildCombinedDateTime = (data) => {
        const { interviewScheduleandTiming, interviewHour, interviewMinute, interviewAmPm } = data;
        if (!interviewScheduleandTiming || !interviewHour || !interviewMinute) return "";
        let hour24 = parseInt(interviewHour, 10);
        if (interviewAmPm === "AM" && hour24 === 12) hour24 = 0;
        if (interviewAmPm === "PM" && hour24 !== 12) hour24 += 12;
        return `${interviewScheduleandTiming}T${String(hour24).padStart(2, "0")}:${interviewMinute}:00`;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const combined = buildCombinedDateTime(formData);

        const payload = {
            userId: user?.userId,
            userName: user?.fullName,
            ...formData,
            interviewScheduleandTiming: combined,
            createdBy: user?._id,
        };

        delete payload.interviewHour;
        delete payload.interviewMinute;
        delete payload.interviewAmPm;

        if (editingHiring) {
            updateHiring.mutate(
                { id: editingHiring.id, formData: payload },
                {
                    onSuccess: () => {
                        setSuccessMsg("Hiring info updated successfully!");
                        setEditingHiring(null);
                        setFormData(emptyForm);
                        setShowPopup(false);
                    },
                    onError: (err) => setErrorMsg(err.message),
                }
            );
        } else {
            createHiring.mutate(payload, {
                onSuccess: () => {
                    setSuccessMsg("Hiring info submitted successfully!");
                    setFormData(emptyForm);
                    setShowPopup(false);
                },
                onError: (err) => setErrorMsg(err.message),
            });
        }
    };

    const handleEdit = (hiring) => {
        let interviewHour = "";
        let interviewMinute = "";
        let interviewAmPm = "AM";
        let interviewDatePart = "";

        if (hiring.interviewScheduleandTiming) {
            const existing = new Date(hiring.interviewScheduleandTiming);
            const h = existing.getHours();
            interviewAmPm = h >= 12 ? "PM" : "AM";
            interviewHour = String(h % 12 || 12).padStart(2, "0");
            interviewMinute = String(existing.getMinutes()).padStart(2, "0");
            interviewDatePart = hiring.interviewScheduleandTiming.split("T")[0];
        }

        setFormData({
            name: hiring.name || "",
            email: hiring.email || "",
            phone: hiring.phone || "",
            gender: hiring.gender || "",
            designation: hiring.designation || "",
            interviewDate: hiring.interviewDate ? hiring.interviewDate.split("T")[0] : "",
            resumeLink: hiring.resumeLink || "",
            portfolioLink: hiring.portfolioLink || "",
            interviewer: hiring.interviewer || "",
            interviewScheduleandTiming: interviewDatePart,
            interviewHour,
            interviewMinute,
            interviewAmPm,
            lastCTC: hiring.lastCTC || "",
            expectedCTC: hiring.expectedCTC || "",
            notes: hiring.notes || "",
            skillknowledge: hiring.skillknowledge || "",
            workExperience: hiring.workExperience || "",
            interviewMode: hiring.interviewMode || "",
            status: hiring.status || "",
        });

        setEditingHiring(hiring);
        setShowPopup(true);
        setSuccessMsg("");
        setErrorMsg("");
    };

    const handleDelete = (hiringId) => {
        if (window.confirm("Are you sure you want to delete this record?")) {
            deleteHiring.mutate(hiringId, {
                onError: (err) => setErrorMsg(err.message),
            });
        }
    };

    const filteredHirings = hirings
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .filter((h) => {
            const matchesSearch =
                h.name?.toLowerCase().includes(search.toLowerCase()) ||
                h.email?.toLowerCase().includes(search.toLowerCase()) ||
                h.designation?.toLowerCase().includes(search.toLowerCase());

            const matchesDesignation = filterDesignation
                ? h.designation?.toLowerCase() === filterDesignation.toLowerCase()
                : true;

            return matchesSearch && matchesDesignation;
        });

    const totalPages = fetching
        ? 1
        : Math.ceil(filteredHirings.length / formsPerPage);
    const currentForms = filteredHirings.slice(
        (currentPage - 1) * formsPerPage,
        currentPage * formsPerPage
    );
    const handlePageChange = (page) => setCurrentPage(page);

    const fieldLabels = {
        name: "Name",
        email: "Email",
        phone: "Phone",
        gender: "Gender",
        designation: "Designation",
        resumeLink: "Resume Link",
        portfolioLink: "Portfolio Link",
        interviewer: "Interviewer",
        interviewScheduleandTiming: "Interview Schedule and Timing",
        lastCTC: "Last CTC / Monthly",
        expectedCTC: "Expected CTC / Monthly",
        notes: "Notes",
        skillknowledge: "Skill / Knowledge",
        workExperience: "Work Experience",
        interviewMode: "Interview Mode",
        status: "Status",
    };

    const handleCommentSubmit = (e) => {
        e.preventDefault();
        if (!selectedHiring) return;

        setSelectedHiring((prev) => ({
            ...prev,
            comments: comment,
        }));

        updateHiring.mutate({
            id: selectedHiring.id,
            comments: comment,
        });
    };

    if (fetching) {
        return (
            <div className="relative h-[90.7vh] p-2 rs:p-6 overflow-y-auto">
                <SkeletonPage />
            </div>
        );
    }

    if (!canViewHome) {
        return (
            <div>
                <div className="relative h-[90.7vh] overflow-hidden z-1">
                    <PermissionPopup />
                </div>
            </div>
        );
    }

    const dropdownOptions = {
        designation: designationList,
        status: ["Rejected", "Hired", "Shortlisted", "Wait for interview"],
        gender: ["Male", "Female", "Other"],
        interviewMode: ["Online", "Offline"],
    };

    const hourOptions = Array.from({ length: 12 }, (_, i) =>
        String(i + 1).padStart(2, "0")
    );
    const minuteOptions = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

    return (
        <div className="relative p-2 rs:p-6">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6">
                <h2 className="text-[15px] sm:text-[20px] font-bold text-gray-800">Hiring Records</h2>

                <div className="flex flex-wrap sm:flex-nowrap gap-3 w-full xl:w-auto">
                    <div className="flex gap-3">
                        <div>
                            <Input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, email"
                                className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-900"
                            />
                        </div>
                        <div>
                            {/* Filter dropdown — driven by the same designationList array */}
                            <select
                                name="designationFilter"
                                value={filterDesignation}
                                onChange={(e) => setFilterDesignation(e.target.value)}
                                className="border rounded px-2 py-2 text-xs sm:text-sm lg:text-sm lg:px-3 lg:py-2 w-full sm:w-auto"
                            >
                                <option value="">All</option>
                                {designationList.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <Button onClick={handleOpen}>+ New Hiring</Button>
                    </div>
                </div>
            </div>

            <div className="border rounded-lg overflow-hidden bg-white">
                <div className="grid grid-flow-col auto-cols-fr gap-4 px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm font-semibold text-gray-500 bg-gray-50">
                    <div>Name</div>
                    <div className="hidden xl:block">Email</div>
                    <div className="hidden xl:block">Phone</div>
                    <div>Designation</div>
                    <div className="hidden xs:block">Interview Date</div>
                    <div className="hidden custom:block">Resume Link</div>
                    <div className="hidden custom:block">Portfolio Link</div>
                    <div className="hidden xs:block">Interviewer</div>
                    <div className="hidden rs:block">Status</div>
                    <div>Action</div>
                </div>

                {fetching ? (
                    <div className="p-4">
                        <SkeletonPage />
                    </div>
                ) : filteredHirings.length > 0 ? (
                    currentForms.map((hiring) => {
                        const statusColor =
                            hiring.status === "Hired"
                                ? "bg-green-100 text-green-700"
                                : hiring.status === "Wait for interview"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : hiring.status === "Rejected"
                                        ? "bg-red-100 text-red-700"
                                        : hiring.status === "Shortlisted"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-800";
                        return (
                            <div
                                key={hiring.id}
                                className="grid grid-flow-col auto-cols-fr gap-4 px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm border-t text-gray-500 bg-gray-50"
                            >
                                <div className="text-gray-700 truncate max-w-[120px]">{hiring.name}</div>
                                <div className="text-gray-700 hidden xl:table-cell truncate max-w-[180px]">{hiring.email}</div>
                                <div className="text-gray-700 hidden xl:table-cell">{hiring.phone}</div>
                                <div className="text-gray-700 truncate max-w-[150px]">{hiring.designation}</div>
                                <div className="text-gray-700 hidden xs:block truncate max-w-[120px]">
                                    {hiring.interviewScheduleandTiming
                                        ? (() => {
                                            const raw = hiring.interviewScheduleandTiming;
                                            const local = raw.replace("T", " ");
                                            return new Date(local).toLocaleString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric",
                                                hour: "2-digit", minute: "2-digit", hour12: true,
                                            });
                                        })()
                                        : "-"}
                                </div>
                                <div className="hidden custom:block">
                                    {hiring.resumeLink ? (
                                        <a href={hiring.resumeLink} target="_blank" rel="noreferrer" className="text-blue-600">
                                            <Link size={16} />
                                        </a>
                                    ) : "-"}
                                </div>
                                <div className="hidden custom:block">
                                    {hiring.portfolioLink ? (
                                        <a href={hiring.portfolioLink} target="_blank" rel="noreferrer" className="text-blue-600">
                                            <Link size={16} />
                                        </a>
                                    ) : "-"}
                                </div>
                                <div className="text-gray-700 hidden xs:block">{hiring.interviewer}</div>
                                <div className="text-gray-700 hidden rs:block">
                                    <span className={`px-2 py-1 text-[11px] sm:text-[13px] rounded-md font-medium ${statusColor}`}>
                                        {hiring.status || "-"}
                                    </span>
                                </div>
                                <div className="text-gray-700 flex gap-0 sm:gap-[3px] items-center">
                                    <div
                                        className="p-1 rounded cursor-pointer hover:bg-orange-100"
                                        onClick={(e) => { e.stopPropagation(); setPreviewHiring(hiring); }}
                                    >
                                        <Eye size={14} color="orange" className="cursor-pointer" />
                                    </div>
                                    <div
                                        className="p-1 rounded cursor-pointer hover:bg-[#e0d4e5]"
                                        onClick={() => {
                                            setSelectedHiring(hiring);
                                            setComment(hiring.comment || "");
                                            setOpenMessage(true);
                                        }}
                                    >
                                        <MessageCircle size={14} color="#ab54d0" className="cursor-pointer" />
                                    </div>
                                    <div className="p-1 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleEdit(hiring)}>
                                        <Pencil size={14} color="blue" className="cursor-pointer" />
                                    </div>
                                    <div className="p-1 rounded cursor-pointer hover:bg-red-100" onClick={() => handleDelete(hiring.id)}>
                                        <Trash2 size={14} color="red" className="cursor-pointer" />
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="px-4 py-4 text-center text-gray-500">No records found</div>
                )}
            </div>

            {/* Preview Modal */}
            {previewHiring && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                    onClick={() => setPreviewHiring(null)}
                >
                    <div
                        className="bg-white relative rounded-lg w-full max-w-[700px] shadow-lg h-[80vh] overflow-hidden overflow-y-auto animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-2 py-1 sm:px-4 sm:py-3">
                            <h2 className="text-white text-[12px] sm:text-lg">Hiring Details</h2>
                            <button className="text-white hover:text-red-400" onClick={() => setPreviewHiring(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="w-full bg-white border-gray-200 rounded-md shadow-sm">
                            <div className="p-4 sm:p-6">
                                <table className="w-full border border-gray-200 rounded-md mb-4 sm:mb-6">
                                    <tbody className="divide-y divide-gray-200">
                                        <InfoRow label="Name" value={previewHiring.name || "—"} />
                                        <InfoRow label="Email" value={previewHiring.email || "—"} />
                                        <InfoRow label="Phone" value={previewHiring.phone || "—"} />
                                        <InfoRow label="Gender" value={previewHiring.gender || "—"} />
                                        <InfoRow label="Designation" value={previewHiring.designation || "—"} />
                                        <InfoRow
                                            label="Resume Link"
                                            value={<span className="break-all">{previewHiring.resumeLink || "—"}</span>}
                                        />
                                        <InfoRow
                                            label="Portfolio Link"
                                            value={<span className="break-all">{previewHiring.portfolioLink || "—"}</span>}
                                        />
                                        <InfoRow label="Interviewer" value={previewHiring.interviewer || "—"} />
                                        <InfoRow
                                            label="Interview Schedule & Timing"
                                            value={
                                                previewHiring.interviewScheduleandTiming
                                                    ? (() => {
                                                        const raw = previewHiring.interviewScheduleandTiming;
                                                        const local = raw.replace("T", " ");
                                                        return (
                                                            new Date(local).toLocaleDateString("en-IN", {
                                                                day: "2-digit",
                                                                month: "2-digit",
                                                                year: "numeric",
                                                            }) +
                                                            " • " +
                                                            new Date(local).toLocaleTimeString("en-IN", {
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                                hour12: true,
                                                            })
                                                        );
                                                    })()
                                                    : "—"
                                            }
                                        />
                                        <InfoRow label="Last CTC / Monthly" value={previewHiring.lastCTC || "—"} />
                                        <InfoRow label="Expected CTC / Monthly" value={previewHiring.expectedCTC || "—"} />
                                        <InfoRow label="Skills / Knowledge" value={previewHiring.skillknowledge || "—"} />
                                        <InfoRow label="Notes" value={previewHiring.notes || "—"} />
                                        <InfoRow label="Work Experience" value={previewHiring.workExperience || "—"} />
                                        <InfoRow label="Interview Mode" value={previewHiring.interviewMode || "—"} />
                                        <InfoRow
                                            label="Status"
                                            value={
                                                <span
                                                    className={`ml-0 px-2 py-1 text-xs rounded ${previewHiring.status === "Hired"
                                                        ? "bg-green-100 text-green-700"
                                                        : previewHiring.status === "Rejected"
                                                            ? "bg-red-100 text-red-700"
                                                            : "bg-yellow-100 text-yellow-700"
                                                        }`}
                                                >
                                                    {previewHiring.status || "—"}
                                                </span>
                                            }
                                        />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Comment Sidebar Overlay */}
            {openMessage && (
                <div
                    className="fixed inset-0 bg-black/40 z-[99999]"
                    onClick={() => setOpenMessage(false)}
                />
            )}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-[420px] bg-white z-[9999999] shadow-xl
                    transform transition-transform duration-300 ease-in-out
                    ${openMessage ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="flex flex-col h-full overflow-y-auto">
                    <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-3 py-2 sm:px-6 sm:py-4 shadow-sm">
                        <h3 className="text-[15px] sm:text-[20px] text-white">Comment</h3>
                        <button onClick={() => setOpenMessage(false)} className="text-gray-300 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="p-2 sm:p-6 flex-1">
                        <form className="space-y-6" onSubmit={handleCommentSubmit}>
                            <div>
                                <label className="block text-gray-700 text-[14px] sm:text-[16px] mb-2">Previous Comment</label>
                                {selectedHiring?.comments ? (
                                    <p className="p-3 bg-gray-100 rounded-md text-sm text-gray-700">{selectedHiring.comments}</p>
                                ) : (
                                    <p className="p-3 bg-gray-50 rounded-md text-sm text-gray-400 italic">No comment yet</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-gray-700 text-[12px] sm:text-[16px] mb-2">Update Comment</label>
                                <textarea
                                    className="w-full border border-gray-300 rounded-md text-[14px] sm:text-[14px] p-3 h-32 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 resize-none placeholder-gray-400"
                                    placeholder="Write your comment..."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-gray-900 text-[12px] sm:text-[16px] hover:bg-gray-600 text-white py-2 rounded-md transition-colors"
                            >
                                Save Comment
                            </Button>
                        </form>
                    </div>
                </div>
            </div>

            {/* New / Edit Hiring Modal */}
            {showPopup && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                    onClick={() => setShowPopup(false)}
                >
                    <div
                        className="bg-white w-full max-w-[820px] rounded-lg shadow-xl transform transition-all duration-300 scale-100 opacity-100 animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 p-2 py-1 sm:px-4 sm:py-3 rounded-t-lg">
                            <h2 className="text-white text-[12px] sm:text-lg">Hiring</h2>
                            <button onClick={() => setShowPopup(false)} className="text-gray-300 hover:text-white transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-2 sm:p-6 h-[90vh] overflow-y-auto">
                            <form
                                className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4"
                                onSubmit={handleSubmit}
                            >
                                {Object.keys(fieldLabels).map((field) => (
                                    <div key={field}>
                                        <label className="block text-[12px] sm:text-[13px] font-medium mb-1">
                                            {fieldLabels[field]}
                                        </label>

                                        {/* Dropdown fields */}
                                        {dropdownOptions[field] ? (
                                            <select
                                                name={field}
                                                value={formData[field]}
                                                onChange={handleChange}
                                                className="w-full border p-2 rounded text-[12px] sm:text-[13px]"
                                            >
                                                <option value="">Select {fieldLabels[field]}</option>
                                                {dropdownOptions[field].map((opt) => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>

                                        ) : field === "notes" || field === "skillknowledge" ? (
                                            /* Textarea fields */
                                            <textarea
                                                name={field}
                                                value={formData[field]}
                                                onChange={handleChange}
                                                placeholder={`Enter ${fieldLabels[field]}`}
                                                rows={3}
                                                className="w-full border p-2 rounded text-[12px] sm:text-[13px]"
                                            />

                                        ) : field === "interviewScheduleandTiming" ? (
                                            /* Custom date + AM/PM time picker */
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    type="date"
                                                    name="interviewScheduleandTiming"
                                                    value={formData.interviewScheduleandTiming}
                                                    onChange={handleChange}
                                                    className="w-full border p-2 rounded text-[12px] sm:text-[13px]"
                                                />
                                                <div className="flex gap-1">
                                                    <select
                                                        name="interviewHour"
                                                        value={formData.interviewHour}
                                                        onChange={handleChange}
                                                        className="flex-1 border p-2 rounded text-[12px] sm:text-[13px]"
                                                    >
                                                        <option value="">HH</option>
                                                        {hourOptions.map((h) => (
                                                            <option key={h} value={h}>{h}</option>
                                                        ))}
                                                    </select>
                                                    <span className="flex items-center text-gray-500 font-bold px-1">:</span>
                                                    <select
                                                        name="interviewMinute"
                                                        value={formData.interviewMinute}
                                                        onChange={handleChange}
                                                        className="flex-1 border p-2 rounded text-[12px] sm:text-[13px]"
                                                    >
                                                        <option value="">MM</option>
                                                        {minuteOptions.map((m) => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        name="interviewAmPm"
                                                        value={formData.interviewAmPm}
                                                        onChange={handleChange}
                                                        className="border p-2 rounded text-[12px] sm:text-[13px] font-semibold"
                                                    >
                                                        <option value="AM">AM</option>
                                                        <option value="PM">PM</option>
                                                    </select>
                                                </div>
                                            </div>

                                        ) : (
                                            /* Default text / email input */
                                            <Input
                                                type={field === "email" ? "email" : "text"}
                                                name={field}
                                                value={formData[field]}
                                                onChange={handleChange}
                                                placeholder={`Enter ${fieldLabels[field]}`}
                                                required={field === "name" || field === "email"}
                                                className="w-full border p-2 rounded text-[12px] sm:text-[13px]"
                                            />
                                        )}
                                    </div>
                                ))}

                                {/* Submit Button */}
                                <div className="col-span-1 md:col-span-2 flex justify-end">
                                    <Button type="submit" variant="success" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <div className="flex items-center gap-2">
                                                Submitting...
                                                <span className="loader-circle"></span>
                                            </div>
                                        ) : (
                                            "Submit"
                                        )}
                                    </Button>
                                </div>

                                {successMsg && (
                                    <p className="col-span-1 md:col-span-2 text-green-600 text-sm mt-1">{successMsg}</p>
                                )}
                                {errorMsg && (
                                    <p className="col-span-1 md:col-span-2 text-red-600 text-sm mt-1">{errorMsg}</p>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
            />
        </div>
    );
}